import { useTranslation } from "react-i18next"

import type { LucideIcon } from "@/components/icons"

import { FileImage, FileText, FileType, Keyboard } from "@/components/icons"

type Format = {
  value: "pdf" | "docx" | "txt" | "paste"
  icon: LucideIcon
  badge: string
  labelKey: string
  hintKey: string
  accent: "primary" | "secondary"
}

const FORMATS: ReadonlyArray<Format> = [
  {
    value: "docx",
    icon: FileType,
    badge: "DOCX",
    labelKey: "format_docx_label",
    hintKey: "format_docx_hint",
    accent: "primary",
  },
  {
    value: "pdf",
    icon: FileText,
    badge: "PDF",
    labelKey: "format_pdf_label",
    hintKey: "format_pdf_hint",
    accent: "secondary",
  },
  {
    value: "txt",
    icon: FileText,
    badge: "TXT",
    labelKey: "format_txt_label",
    hintKey: "format_txt_hint",
    accent: "secondary",
  },
  {
    value: "paste",
    icon: Keyboard,
    badge: "TEXT",
    labelKey: "format_paste_label",
    hintKey: "format_paste_hint",
    accent: "secondary",
  },
] as const

export function DemoTranslatorFormats() {
  const { t } = useTranslation("", { keyPrefix: "training.ai-translator.introduction" })

  return (
    <div className="w-full">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {FORMATS.map(({ value, icon: Icon, badge, labelKey, hintKey, accent }) => {
          const isPrimary = accent === "primary"
          return (
            <div
              key={value}
              className={
                isPrimary
                  ? "bg-primary/5 border-primary/30 flex flex-col items-start gap-2 rounded-xl border p-3"
                  : "bg-background flex flex-col items-start gap-2 rounded-xl border p-3"
              }
            >
              <div
                className={
                  isPrimary
                    ? "bg-primary/15 text-primary flex h-9 w-9 items-center justify-center rounded-lg"
                    : "bg-muted text-muted-foreground flex h-9 w-9 items-center justify-center rounded-lg"
                }
              >
                <Icon className="size-5" />
              </div>
              <span
                className={
                  isPrimary
                    ? "text-primary text-[10px] font-bold tracking-wide uppercase"
                    : "text-muted-foreground text-[10px] font-bold tracking-wide uppercase"
                }
              >
                {badge}
              </span>
              <p className="text-sm leading-tight font-semibold">{t(labelKey)}</p>
              <p className="text-muted-foreground text-xs">{t(hintKey)}</p>
            </div>
          )
        })}
      </div>
      <div className="bg-muted/40 mt-3 flex items-center gap-3 rounded-lg border p-3">
        <FileImage className="text-muted-foreground size-5 shrink-0" />
        <p className="text-muted-foreground text-xs">{t("format_images_note")}</p>
      </div>
    </div>
  )
}
